import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

import { GWPTierChangeEvent, GWPGiftAddedEvent, GWPGiftSwappedEvent, GWPGiftRemovedEvent } from '@dkl/events';
import { DklWidgetBase } from '@dkl/global';

/**
 * Discount Kit - GWP (Cart) widget
 *
 * Emits tier-change events based on the cart subtotal (items_subtotal_price)
 * relative to configured GWP tiers.
 */
class DklGwp extends DklWidgetBase {
  /** @type {Array<{tierLevel: number, threshold: number, buysType: string, message: string, getsType: string, getsValue: number, getsQuantity: number, productIds: number[]}>} */
  #tiers = [];

  /** @type {{tierLevel: number, threshold: number, buysType: string, message: string, getsType: string, getsValue: number, getsQuantity: number, productIds: number[]} | null} */
  #lastTierMet = null;

  /** @type {boolean} */
  #isInitialLoad = true;

  /** @type {boolean} */
  #isCleaningUp = false;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#parseTiers();

    // Create a new AbortController for this connection
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Use event delegation for the select gift button (it may be morphed)
    this.addEventListener('click', this.#handleClick, { signal });

    // Stop all variant events from bubbling out of this element to prevent interference with product pages
    this.addEventListener('variant:update', (e) => {
      e.stopPropagation();
    }, { signal, capture: true });
    
    this.addEventListener('variant:selected', (e) => {
      e.stopPropagation();
    }, { signal, capture: true });

    const initialValue = this.#getInitialCartValue();
    this.#updateForCartValue(initialValue);

    // Dispatch mount with current tier info
    const widgetType = this.dataset.dklWidgetType || 'gwp';
    this.dispatchWidgetMount(widgetType, {
      currentTier: this.#lastTierMet,
      cartCurrentValue: initialValue,
    });
  }

  disconnectedCallback() {
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
    const widgetType = this.dataset.dklWidgetType || 'gwp';
    this.dispatchWidgetUnmount(widgetType);
  }

  /**
   * Parse tier data from child elements rendered by the Liquid snippet.
   */
  #parseTiers() {
    const tierEls = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.dkl-gwp__tier')
    );

    this.#tiers = Array.from(tierEls).map((el, index) => {
      const threshold = Number.parseInt(el.dataset.tierThreshold || '0', 10) || 0;
      const buysType = el.dataset.tierBuysType || 'minimumSubtotal';
      const message = el.dataset.tierMessage || '';
      const getsType = el.dataset.tierGetsType || 'percentage';
      const getsValue = Number.parseFloat(el.dataset.tierGetsValue || '100') || 100;
      const getsQuantity = Number.parseInt(el.dataset.tierGetsQuantity || '1', 10) || 1;
      const productIds = (el.dataset.tierProductIds || '').split(',').filter(Boolean).map(id => Number(id));

      return {
        tierLevel: index, 
        threshold,
        buysType,
        message,
        getsType,
        getsValue,
        getsQuantity,
        productIds,
      };
    });

    // Ensure tiers are sorted by threshold ascending
    this.#tiers.sort((a, b) => a.threshold - b.threshold);
  }

  /**
   * Returns the initial cart value (subtotal in cents or item count) from the data attribute.
   *
   * @returns {number}
   */
  #getInitialCartValue() {
    const raw = this.dataset.dklCartCurrentValue;
    const value = Number.parseInt(raw || '0', 10);
    return Number.isFinite(value) ? value : 0;
  }


  /**
   * Handle click events (using delegation for dynamically rendered buttons)
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    
    const selectButton = target.closest('[data-dkl-action="select-gift"]');
    const editButton = target.closest('[data-dkl-action="edit-gift"]');
    
    if (selectButton || editButton) {
      const button = /** @type {HTMLButtonElement} */ (selectButton || editButton);
      
      // Don't do anything if already loading
      if (button.classList.contains('loading')) {
        return;
      }
      
      event.preventDefault();
      event.stopPropagation();
      
      // Get the tier index from the button
      const tierIndex = Number(button.dataset.tierIndex);
      
      this.#openGiftSelector(tierIndex);
    }
  };

  /**
   * Open the gift selector for a specific tier
   * @param {number} tierIndex - The tier index to open selector for
   */
  #openGiftSelector(tierIndex) {
    const panel = /** @type {HTMLElement | null} */ (document.querySelector('[data-cart-drawer-gift-selector]'));
    const mainCart = /** @type {HTMLElement | null} */ (document.querySelector('[data-cart-drawer-main]'));
    const panelProductsContainer = /** @type {HTMLElement | null} */ (document.getElementById('gwp-gift-products-container'));
    
    // Find the products container in this element
    const productsContainer = /** @type {HTMLElement | null} */ (this.querySelector('[data-gwp-gift-products]'));
    
    if (!panel || !mainCart || !panelProductsContainer || !productsContainer) {
      return;
    }

    // Move the products container into the panel and show it
    productsContainer.hidden = false;
    panelProductsContainer.appendChild(productsContainer);

    // Store the active tier on the panel
    panel.dataset.activeTier = String(tierIndex);

    // Hide all products first
    const allWrappers = productsContainer.querySelectorAll('[data-gwp-product-wrapper]');
    allWrappers.forEach(wrapper => {
      /** @type {HTMLElement} */ (wrapper).hidden = true;
    });

    // Show only products for the clicked tier
    const tierWrappers = productsContainer.querySelectorAll(`[data-gwp-product-wrapper][data-product-tier="${tierIndex}"]`);
    tierWrappers.forEach(wrapper => {
      /** @type {HTMLElement} */ (wrapper).hidden = false;
    });

    // Set up all event listeners (variant pickers, button states, click handlers)
    // This must be called AFTER showing/hiding products
    this.#setupGiftSelectorListeners(productsContainer, tierIndex);

    // Show gift selector panel (remove hidden first for transition to work)
    panel.hidden = false;
    
    // Trigger transition on next frame
    requestAnimationFrame(() => {
      mainCart.classList.add('cart-drawer__inner--gift-selector-active');
      panel.classList.add('cart-drawer__gift-selector--active');
      
      // Focus the back button after transition starts
      setTimeout(() => {
        const backButton = panel.querySelector('[data-dkl-action="close-gift-selector"]');
        if (backButton) {
          /** @type {HTMLElement} */ (backButton).focus();
        }
      }, 50);
    });
  };

  /**
   * Set up gift selector listeners
   * @param {HTMLElement} productsContainer
   * @param {number} tierIndex
   */
  #setupGiftSelectorListeners(productsContainer, tierIndex) {
    // Set up back button
    const panel = document.querySelector('[data-cart-drawer-gift-selector]');
    if (panel) {
      const backButton = panel.querySelector('[data-dkl-action="close-gift-selector"]');
      if (backButton) {
        const backHandler = () => this.#closeGiftSelector();
        backButton.removeEventListener('click', backHandler);
        backButton.addEventListener('click', backHandler);
      }
      
      // Set up focus trap
      this.#setupFocusTrap(/** @type {HTMLElement} */ (panel));
    }
    
    // Set up variant pickers and button states for visible products
    if (productsContainer) {
      const visibleWrappers = productsContainer.querySelectorAll('[data-gwp-product-wrapper]:not([hidden])');
      const discountTitle = this.dataset.dklDiscountTitle || '';
      const tierIdentifier = `${discountTitle}_tier_${tierIndex}`;
      
      // Fetch cart to check which variant is already in cart for this tier
      fetch(`/cart.js`)
        .then(response => response.json())
        .then(cartData => {
          const giftInCart = cartData.items.find(
            /** @param {any} item */
            (item) => item.properties && item.properties._gwp_tier === tierIdentifier
          );
          const giftVariantIdInCart = giftInCart ? String(giftInCart.variant_id) : null;
          
          visibleWrappers.forEach((wrapper) => {
            const productCard = wrapper.querySelector('[data-product-id]');
            if (!productCard) return;
            
            // Set up dynamic button state
            const updateButtonState = () => {
              const addButtons = productCard.querySelectorAll('[data-action="add-gift"]');
              
              let selectedVariantId = null;
              const checkedRadio = productCard.querySelector('input[type="radio"]:checked');
              if (checkedRadio) {
                selectedVariantId = checkedRadio.getAttribute('data-variant-id');
              } else {
                const hiddenVariantInput = /** @type {HTMLInputElement | null} */ (productCard.querySelector('input[name="id"]'));
                selectedVariantId = hiddenVariantInput?.value || null;
              }
              
              const isCurrentVariantInCart = giftVariantIdInCart && selectedVariantId === giftVariantIdInCart;
              
              addButtons.forEach((btn) => {
                const button = /** @type {HTMLButtonElement} */ (btn);
                if (isCurrentVariantInCart) {
                  button.disabled = true;
                  button.textContent = 'Already Selected';
                  button.classList.add('disabled');
                } else {
                  button.disabled = false;
                  button.textContent = button.classList.contains('dkl-gwp-product__add-button-quick') ? 'Select' : 'Select This Gift';
                  button.classList.remove('disabled');
                }
              });
            };
            
            updateButtonState();
            productCard.addEventListener('variant:selected', updateButtonState);
          });
        })
        .catch(error => {
          console.error('[GWP] Failed to fetch cart data:', error);
        });
    }

    // Remove any existing click handler
    if (panel) {
      const existingHandler = /** @type {any} */ (panel)._gwpClickHandler;
      if (existingHandler) {
        panel.removeEventListener('click', existingHandler);
      }
    }

    // Create the click handler
    const clickHandler = async (/** @type {MouseEvent} */ e) => {
      // Stop propagation to prevent bubbling to dkl-gwp element
      e.stopPropagation();
      const target = /** @type {HTMLElement} */ (e.target);
      
      const addButton = target.closest('[data-action="add-gift"]');
      if (addButton) {
        e.preventDefault();
        e.stopPropagation();
        
        const productCard = addButton.closest('.dkl-gwp-product');
        if (!productCard) {
          return;
        }
        
        const productId = productCard.getAttribute('data-product-id');
        
        let variantId = '';
        
        // Gget variant ID from checked radio button (multi-variant products)
        const checkedRadio = /** @type {HTMLInputElement | null} */ (
          productCard.querySelector('input[type="radio"]:checked')
        );
        
        if (checkedRadio) {
          variantId = checkedRadio.dataset.variantId || '';
        } else {
          // Fallback to hidden input for single-variant products
          const hiddenInput = /** @type {HTMLInputElement | null} */ (
            productCard.querySelector('input[name="id"][data-gwp-variant-id]')
          );
          
          if (hiddenInput) {
            variantId = hiddenInput.value || '';
          }
        }
        
        if (!variantId || !productId) {
          return;
        }
        
        // Add loading state to button
        const button = /** @type {HTMLButtonElement} */ (addButton);
        this.#setButtonLoading(button, true);
        
        await this.#addGiftToCart(variantId, productId, tierIndex);
        this.#setButtonLoading(button, false);
        return;
      }
      
      // Check if clicking the toggle button to expand/collapse variants
      const toggleButton = target.closest('[data-action="toggle-product"]');
      if (toggleButton) {
        e.preventDefault();
        e.stopPropagation();
        
        const productCard = toggleButton.closest('[data-product-id]');
        if (!productCard) return;
        
        this.#toggleProductExpanded(productCard);
        return;
      }
    };
    
    // Store handler reference for cleanup and add to panel
    if (panel) {
      /** @type {any} */ (panel)._gwpClickHandler = clickHandler;
      panel.addEventListener('click', clickHandler);
    }
  }

  /**
   * Set up focus trap for the gift selector panel
   * @param {HTMLElement} panel
   */
  #setupFocusTrap(panel) {
    // Remove any existing focus trap listener
    if (/** @type {any} */ (panel)._focusTrapHandler) {
      document.removeEventListener('keydown', /** @type {any} */ (panel)._focusTrapHandler);
    }

    const focusTrapHandler = (/** @type {KeyboardEvent} */ e) => {
      if (e.key !== 'Tab' || panel.hidden) return;

      const focusableElements = panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      const focusableArray = Array.from(focusableElements);
      const firstFocusable = focusableArray[0];
      const lastFocusable = focusableArray[focusableArray.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          /** @type {HTMLElement} */ (lastFocusable).focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          /** @type {HTMLElement} */ (firstFocusable).focus();
        }
      }
    };

    // Store handler reference for cleanup
    /** @type {any} */ (panel)._focusTrapHandler = focusTrapHandler;
    document.addEventListener('keydown', focusTrapHandler);
  }

  /**
   * Close the gift selector panel and show main cart
   */
  #closeGiftSelector() {
    const panel = /** @type {HTMLElement | null} */ (document.querySelector('[data-cart-drawer-gift-selector]'));
    const mainCart = /** @type {HTMLElement | null} */ (document.querySelector('[data-cart-drawer-main]'));
    const panelProductsContainer = /** @type {HTMLElement | null} */ (document.getElementById('gwp-gift-products-container'));
    const productsContainer = /** @type {HTMLElement | null} */ (panelProductsContainer?.querySelector('[data-gwp-gift-products]'));
    
    if (panel && mainCart) {
      // Remove focus trap
      if (/** @type {any} */ (panel)._focusTrapHandler) {
        document.removeEventListener('keydown', /** @type {any} */ (panel)._focusTrapHandler);
        /** @type {any} */ (panel)._focusTrapHandler = null;
      }
      
      // Remove active classes to trigger slide-out transition
      panel.classList.remove('cart-drawer__gift-selector--active');
      mainCart.classList.remove('cart-drawer__inner--gift-selector-active');
      
      // Wait for transition to complete before hiding and moving products
      setTimeout(() => {
        panel.hidden = true;
        
        // Move products back to dkl-gwp element and hide them
        if (productsContainer) {
          productsContainer.hidden = true;
          this.appendChild(productsContainer);
        }
      }, 300);
    }
  }

  /**
   * Toggle product expanded state
   * @param {Element} productCard
   */
  #toggleProductExpanded(productCard) {
    const expanded = productCard.querySelector('.dkl-gwp-product__expanded');
    const isExpanded = !expanded?.hasAttribute('hidden');

    // Collapse all others first
    const dialog = productCard.closest('dialog');
    if (dialog) {
      const allProducts = dialog.querySelectorAll('[data-product-id]');
      allProducts.forEach((card) => {
        if (card !== productCard) {
          card.querySelector('.dkl-gwp-product__expanded')?.setAttribute('hidden', '');
          card.classList.remove('expanded');
        }
      });
    }

    // Toggle this one
    if (isExpanded) {
      expanded?.setAttribute('hidden', '');
      productCard.classList.remove('expanded');
    } else {
      expanded?.removeAttribute('hidden');
      productCard.classList.add('expanded');
    }
  }

  /**
   * Set button loading state
   * @param {HTMLButtonElement} button
   * @param {boolean} loading
   */
  #setButtonLoading(button, loading) {
    if (loading) {
      button.classList.add('loading');
      button.disabled = true;
      button.setAttribute('data-original-text', button.textContent || '');
      button.innerHTML = `
        <span class="dkl-gwp__loading-spinner"></span>
        <span>Adding...</span>
      `;
    } else {
      button.classList.remove('loading');
      button.disabled = false;
      const originalText = button.getAttribute('data-original-text');
      if (originalText) {
        button.textContent = originalText;
        button.removeAttribute('data-original-text');
      }
    }
  }

  /**
   * Add gift to cart
   * @param {string} variantId
   * @param {string} productId
   * @param {number} tierIndex
   */
  async #addGiftToCart(variantId, productId, tierIndex) {
    const discountTitle = this.dataset.dklDiscountTitle || '';
    const tierIdentifier = `${discountTitle}_tier_${tierIndex}`;

    // Apply skeleton loader to this tier
    this.#shimmerTiers([tierIndex]);

    // Check if we're swapping (removing an existing gift)
    const existingGift = await this.#getExistingGift(tierIndex);
    const isSwapping = existingGift !== null;

    // First, remove any existing gift for this tier if editing
    await this.#removeExistingGift(tierIndex);

    try {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      formData.append('properties[_gift]', 'true');
      formData.append('properties[_gwp_tier]', tierIdentifier);
      formData.append('properties[_gwp_discount]', discountTitle);
      
      // Add sections parameter to get updated cart HTML
      formData.append('sections', this.#getSectionsToRender().join(','));
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(`${Theme.routes.cart_add_url}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add gift: ${response.status}`);
      }

      const responseData = await response.json();

      this.#closeGiftSelector();

      // Import CartAddEvent dynamically (theme event)
      const { CartAddEvent } = await import('@theme/events');
      
      // Dispatch CartAddEvent to trigger cart drawer update with sections
      const cartAddEvent = new CartAddEvent({}, variantId, {
        source: 'gwp-selector',
        itemCount: 1,
        productId: productId,
        sections: responseData.sections,
      });
      document.dispatchEvent(cartAddEvent);

      // Get cart current value
      const cartCurrentValue = this.#getInitialCartValue();

      // Get the tier to include buysType
      const tier = this.#tiers[tierIndex];
      const buysType = tier?.buysType || this.dataset.dklBuysType || 'minimumSubtotal';

      // Dispatch appropriate GWP event
      if (isSwapping && existingGift) {
        const resource = {
          widgetId: this.widgetId,
          widgetType: 'gwp',
          tierIndex,
          tierIdentifier,
          oldVariant: {
            variantId: Number(existingGift.variant_id),
            productId: Number(existingGift.product_id),
          },
          newVariant: {
            variantId: Number(variantId),
            productId: Number(productId),
          },
          discountTitle,
          buysType,
          cartCurrentValue,
        };
        this.log('gwp:gift-swapped', resource);
        this.dispatchEvent(new GWPGiftSwappedEvent(resource));
      } else {
        const resource = {
          widgetId: this.widgetId,
          widgetType: 'gwp',
          tierIndex,
          tierIdentifier,
          variant: {
            variantId: Number(variantId),
            productId: Number(productId),
          },
          discountTitle,
          buysType,
          cartCurrentValue,
        };
        this.log('gwp:gift-added', resource);
        this.dispatchEvent(new GWPGiftAddedEvent(resource));
      }
    } catch (error) {
      console.error('[GWP] Error adding gift:', error);
      alert('Failed to add gift to cart. Please try again.');
    }
  }

  /**
   * Get sections to render for cart updates
   * @returns {string[]}
   */
  #getSectionsToRender() {
    return [
      document.getElementById('cart-drawer')?.dataset.id || 'cart-drawer',
      document.getElementById('cart-icon-bubble')?.dataset.id || 'cart-icon-bubble'
    ].filter(Boolean);
  };

  /**
   * Get existing gift for a tier
   * @param {number} tierIndex
   * @returns {Promise<any | null>}
   */
  async #getExistingGift(tierIndex) {
    try {
      // Use /cart.js directly for JSON response
      const timestamp = Date.now();
      const cartUrl = `/cart.js?t=${timestamp}`;
      
      const response = await fetch(cartUrl);
      
      if (!response.ok) {
        return null;
      }
      
      const cartData = await response.json();
      
      const discountTitle = this.dataset.dklDiscountTitle || '';
      const tierIdentifier = `${discountTitle}_tier_${tierIndex}`;
      
      const existingGift = cartData.items.find(
        /** @param {any} item */
        (item) => item.properties && item.properties._gwp_tier === tierIdentifier
      );
      
      return existingGift || null;
    } catch (error) {
      console.error('[GWP] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Remove existing gift for a tier before adding a new one
   * @param {number} tierIndex
   */
  async #removeExistingGift(tierIndex) {
    try {
      const discountTitle = this.dataset.dklDiscountTitle || '';
      const tierIdentifier = `${discountTitle}_tier_${tierIndex}`;
      
      // Fetch current cart to find the line item
      const cartResponse = await fetch(`/cart.js`);
      const cart = await cartResponse.json();
      
      // Find line item with matching tier identifier
      const giftItem = cart.items.find((/** @type {any} */ item) => 
        item.properties?._gwp_tier === tierIdentifier
      );
      
      if (giftItem) {
        // Remove the item
        const formData = new FormData();
        formData.append('id', giftItem.key);
        formData.append('quantity', '0');
        
        await fetch('/cart/change.js', {
          method: 'POST',
          body: formData,
        });
      }
    } catch (error) {
      console.error('[GWP] Error removing existing gift:', error);
    }
  };

  /**
   * Clean up gift items from cart when their tier is no longer met
   * Removes the entire item instead of just the properties
   * @param {any} cartObj
   */
  async #cleanupGiftProperties(cartObj) {
    if (!cartObj?.items || !Array.isArray(cartObj.items)) {
      return;
    }

    // Get the current discount title for this widget
    const currentDiscountTitle = this.dataset.dklDiscountTitle || '';
    
    if (!currentDiscountTitle) {
      return;
    }

    const itemsToRemove = cartObj.items.filter(
      /** @param {any} item */
      (item) => {
        // Check if item has gift properties
        const hasGiftProperties = item.properties?._gift === 'true' && item.properties?._gwp_tier && item.properties?._gwp_discount;
        if (!hasGiftProperties) return false;

        // Only clean up items from THIS discount (match by title)
        const isFromThisDiscount = item.properties._gwp_discount === currentDiscountTitle;
        if (!isFromThisDiscount) return false;

        // Get the tier this gift belongs to
        // The _gwp_tier property is in format: "DiscountTitle_tier_0"
        const tierIdentifier = item.properties._gwp_tier;
        const tierMatch = tierIdentifier.match(/_tier_(\d+)$/);
        if (!tierMatch) return false;
        
        const giftTierIndex = parseInt(tierMatch[1], 10);
        if (!Number.isFinite(giftTierIndex)) return false;

        // Check if we've reached this tier based on current cart value (excluding gift items)
        const buysType = this.dataset.dklBuysType;
        const currentValue = buysType === 'minimumQuantity'
          ? cartObj.items.reduce((/** @type {number} */ sum, /** @type {any} */ i) => i.properties?._gift === 'true' ? sum : sum + i.quantity, 0)
          : cartObj.items.reduce((/** @type {number} */ sum, /** @type {any} */ i) => i.properties?._gift === 'true' ? sum : sum + i.final_line_price, 0);

        // Find the tier threshold
        const giftTier = this.#tiers[giftTierIndex];
        if (!giftTier) return false;

        // Only remove if we haven't reached this tier's threshold
        const shouldRemove = currentValue < giftTier.threshold;
        return shouldRemove;
      }
    );

    if (itemsToRemove.length === 0) return;

    // Get unique tier indexes that are being affected
    const affectedTierIndexes = [...new Set(
      itemsToRemove.map((/** @type {any} */ item) => {
        const tierIdentifier = item.properties._gwp_tier;
        const tierMatch = tierIdentifier.match(/_tier_(\d+)$/);
        return tierMatch ? parseInt(tierMatch[1], 10) : null;
      }).filter((/** @type {any} */ idx) => idx !== null)
    )];

    // Apply skeleton loader to affected tiers only
    this.#shimmerTiers(affectedTierIndexes);

    // Set flag to prevent recursive cart update handling
    this.#isCleaningUp = true;

    // Build updates object to batch all removals in one request
    /** @type {Record<string, number>} */
    const updates = {};
    itemsToRemove.forEach((/** @type {any} */ item) => {
      updates[item.key] = 0;
    });

    try {
      // Get the cart items section ID
      const cartItemsComponent = /** @type {HTMLElement | null} */ (document.querySelector('cart-items-component'));
      const sectionId = cartItemsComponent?.dataset.sectionId;
      
      // Use /cart/update.js to batch remove all items at once, requesting sections
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          updates,
          sections: sectionId ? [sectionId, 'cart-icon-bubble'] : ['cart-icon-bubble']
        }),
      });

      const result = await response.json();

      // Check for errors in the response
      if (result.errors) {
        console.error('[GWP] Error from cart API:', result.errors);
        return;
      }

      // Calculate cart current value (excluding gifts)
      const widgetBuysType = this.dataset.dklBuysType;
      const cartCurrentValue = widgetBuysType === 'minimumQuantity'
        ? result.items.reduce((/** @type {number} */ sum, /** @type {any} */ i) => i.properties?._gift === 'true' ? sum : sum + i.quantity, 0)
        : result.items.reduce((/** @type {number} */ sum, /** @type {any} */ i) => i.properties?._gift === 'true' ? sum : sum + i.final_line_price, 0);
      
      // Dispatch gift removed events for each item
      itemsToRemove.forEach((/** @type {any} */ item, /** @type {number} */ idx) => {
        const tierIndex = parseInt(item.properties._gwp_tier?.split('_tier_')[1], 10) || 0;
        const tier = this.#tiers[tierIndex];
        const tierBuysType = tier?.buysType || widgetBuysType || 'minimumSubtotal';

        const resource = {
          widgetId: this.dataset.dklWidgetId || '',
          widgetType: this.dataset.dklWidgetType || 'gwp',
          tierIndex,
          tierIdentifier: item.properties._gwp_tier || '',
          variant: {
            variantId: Number(item.variant_id),
            productId: Number(item.product_id),
          },
          discountTitle: currentDiscountTitle,
          buysType: tierBuysType,
          cartCurrentValue,
          reason: 'tier-no-longer-met'
        };

        this.dispatchEvent(new GWPGiftRemovedEvent(resource));
        this.log('gwp:gift-removed', resource);
      });
      
      // Reset flag before dispatching cart update
      this.#isCleaningUp = false;
      
      // Dispatch CartUpdateEvent with sections to trigger proper cart UI update
      document.dispatchEvent(
        new CartUpdateEvent(result, sectionId || '', {
          itemCount: result.item_count || 0,
          source: 'dkl-gwp-cleanup',
          sections: result.sections
        })
      );
      
      // Update GWP widget state with value excluding gift items (reuse cartCurrentValue from above)
      if (Number.isFinite(cartCurrentValue)) {
        this.#updateForCartValue(Number(cartCurrentValue));
      }
      
      // Remove shimmer from affected tiers
      this.#removeShimmer(affectedTierIndexes);
    } catch (error) {
      console.error('[GWP] Error removing gift items:', error);
      this.#isCleaningUp = false;
      this.#removeShimmer();
    }
  }

  /**
   * Apply skeleton loader to specific tier(s)
   * @param {number[]} [tierIndexes] - Array of tier indexes to shimmer. If not provided, shimmers all tiers.
   */
  #shimmerTiers(tierIndexes) {
    if (tierIndexes && tierIndexes.length > 0) {
      // Shimmer specific tiers
      tierIndexes.forEach(index => {
        const tier = this.querySelector(`[data-tier-index="${index}"] .dkl-gwp__tier-inner`);
        if (tier) {
          tier.classList.add('dkl-gwp__tier-inner--skeleton');
        }
      });
    } else {
      // Shimmer all tiers
      const tierInners = this.querySelectorAll('.dkl-gwp__tier-inner');
      tierInners.forEach(tierInner => {
        tierInner.classList.add('dkl-gwp__tier-inner--skeleton');
      });
    }
  }

  /**
   * Remove skeleton loader from specific tier(s)
   * @param {number[]} [tierIndexes] - Array of tier indexes to remove shimmer from. If not provided, removes from all tiers.
   */
  #removeShimmer(tierIndexes) {
    if (tierIndexes && tierIndexes.length > 0) {
      // Remove shimmer from specific tiers
      tierIndexes.forEach(index => {
        const tier = this.querySelector(`[data-tier-index="${index}"] .dkl-gwp__tier-inner`);
        if (tier) {
          tier.classList.remove('dkl-gwp__tier-inner--skeleton');
        }
      });
    } else {
      // Remove shimmer from all tiers
      const tierInners = this.querySelectorAll('.dkl-gwp__tier-inner');
      tierInners.forEach(tierInner => {
        tierInner.classList.remove('dkl-gwp__tier-inner--skeleton');
      });
    }
  }

  /**
   * Morph GWP widget content with updated sections
   * @param {Record<string, string>} sections
   */
  async #morphGWPContent(sections) {
    // Find the section that contains this GWP widget
    const section = this.closest('.shopify-section');
    if (!section) return;

    const sectionId = section.id.replace('shopify-section-', '');
    const sectionHTML = sections[sectionId] || sections[`shopify-section-${sectionId}`];
    
    if (!sectionHTML) return;

    // Parse the new HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHTML, 'text/html');
    const newGWP = doc.querySelector('dkl-gwp');
    
    if (!newGWP) return;
    
    // Morph only the children to preserve the custom element and event listeners
    morph(this, newGWP, { childrenOnly: true });
    
    // Wait for next frame to ensure DOM has settled
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => requestAnimationFrame(resolve));
    
    // Re-parse tiers after morphing
    this.#parseTiers();
  }

  /**
   * Handle cart update events from the Horizon theme.
   *
   * Prefers the cart object from the event payload as the source of truth, if it exists (there are some edge 
   * cases where Shopify doesn't include the cart object in the event payload if requests are stale or something else has gone wrong).
   * If the payload does not include a usable subtotal, falls back to
   * fetching `/cart.js` to compute the current subtotal.
   *
   * @param {Event} event
   */
  #onCartUpdate = (event) => {
    /** @type {any} */
    const e = event;
    const detail = e?.detail;
    let cart = detail?.resource;
    const sections = detail?.data?.sections;

    // Don't process if we're currently cleaning up to avoid loops
    if (this.#isCleaningUp) {
      return;
    }

    /**
     * @param {any} cartObj
     */
    const resolveAndUpdate = async (cartObj) => {
      // If sections are available, morph the GWP widget content first
      if (sections) {
        await this.#morphGWPContent(sections);
      }

      const buysType = this.dataset.dklBuysType;
      // Calculate current value excluding gift items
      let currentValue;

      if (buysType === 'minimumQuantity') {
        currentValue = cartObj?.items 
          ? cartObj.items.reduce((/** @type {number} */ sum, /** @type {any} */ item) => item.properties?._gift === 'true' ? sum : sum + item.quantity, 0)
          : null;
      } else {
        currentValue = cartObj?.items 
          ? cartObj.items.reduce((/** @type {number} */ sum, /** @type {any} */ item) => item.properties?._gift === 'true' ? sum : sum + item.final_line_price, 0)
          : null;
      }

      if (!Number.isFinite(currentValue)) return;
      this.#updateForCartValue(Number(currentValue));
      
      // Clean up gift items from items without discounts
      await this.#cleanupGiftProperties(cartObj);
      
      // Wait a bit longer to ensure all DOM updates are complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Remove shimmer after everything is complete
      this.#removeShimmer();
    };

    // If the event already has a cart payload with the needed value, use it directly.
    if (cart) {
      const buysType = this.dataset.dklBuysType;
      const hasValue = buysType === 'minimumQuantity' 
        ? Number.isFinite(cart.item_count)
        : Number.isFinite(cart.items_subtotal_price);
      
      if (hasValue) {
        resolveAndUpdate(cart);
        return;
      }
    }

    // Fetch the cart state from `/cart.js`.
    fetch(`/cart.js`)
      .then((response) => {
        if (!response.ok) return null;
        return response.json();
      })
      .then((fetchedCart) => {
        if (!fetchedCart) return;
        resolveAndUpdate(fetchedCart);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to resolve cart value for GWP widget:', error);
      });
  };

  /**
   * Compute current tier from cart value and emit tier-change events.
   *
   * @param {number} currentValue - Current cart value (subtotal or quantity)
   */
  #updateForCartValue(currentValue) {
    /** @type {any} */
    let currentTier = null;
    for (const tier of this.#tiers) {
      if (currentValue >= tier.threshold) {
        currentTier = tier;
      } else {
        break;
      }
    }

    const previousTier = this.#lastTierMet;
    const previousLevel = previousTier ? previousTier.tierLevel : -1;
    const currentLevel = currentTier ? currentTier.tierLevel : -1;

    // On initial load, just store the current tier
    if (this.#isInitialLoad) {
      this.#lastTierMet = currentTier;
      this.#isInitialLoad = false;
      return;
    }

    // Skip if tier hasn't changed
    if (previousLevel === currentLevel) {
      return;
    }

    this.#lastTierMet = currentTier;

    // Dispatch tier change event
    const buysType = this.dataset.dklBuysType || 'minimumSubtotal';
    const resource = {
      widgetId: this.widgetId,
      widgetType: this.dataset.dklWidgetType || 'gwp',
      previousTier,
      currentTier,
      buysType,
      cartCurrentValue: currentValue,
    };

    this.log('gwp:tier-change', resource);
    this.dispatchEvent(new GWPTierChangeEvent(resource));
  }

}

if (typeof customElements !== 'undefined' && !customElements.get('dkl-gwp')) {
  customElements.define('dkl-gwp', DklGwp);
}